import { makeObservable, observable, action, computed, autorun, reaction } from 'mobx';
import { Subject } from 'rxjs';
import { message } from 'antd';
import { BaseCompanySegregationStateDto, BaseCompanyDto } from '../types';
import { Disposer } from '../../custom_shared/misc';
import { ContractsIngestionService } from '../services';
import AppStore from './AppStore';
import SessionStore from './SessionStore';
import ContractSubmissionStore from './ContractSubmissionStore';

export default class BaseCompanySegregationStore extends Disposer {
    state: BaseCompanySegregationStateDto = {
        selectedBaseCompanyId: null,
        showBaseCompanySelectModal: false,
        error: null
    };

    baseCompanies: BaseCompanyDto[] = [];

    loadingBaseCompanies: boolean = false;

    loadingState: boolean = false;

    readonly baseCompanyChangeSubject = new Subject<{ selectedBaseCompanyId: string }>();

    constructor(
        private readonly appStore: AppStore,
        private readonly sessionStore: SessionStore,
        private readonly contractSubmissionStore: ContractSubmissionStore,
        private readonly service: ContractsIngestionService
    ) {
        super();

        makeObservable(this, {
            state: observable,
            baseCompanies: observable,
            loadingBaseCompanies: observable,
            loadingState: observable,
            setState: action,
            setBaseCompanies: action,
            setLoadingBaseCompanies: action,
            setLoadingState: action,
            isInitializing: computed,
            useBaseCompanySegregation: computed,
            baseCompanyOptions: computed,
            switchHidden: computed,
            isSelectionDisabled: computed
        });

        this.reactions.push(
            autorun(r => {
                if (this.useBaseCompanySegregation) {
                    this.getState().then(() => this.initialize());
                    r.dispose();
                }
            })
        );

        this.reactions.push(
            reaction(
                () => this.state.error,
                error => {
                    if (!error || this.appStore.sessionState === 'Archived') {
                        return;
                    }

                    this.sessionStore.updateSession(sessionStore.sessionId, 'Archived');
                }
            )
        );
    }

    setState(state: BaseCompanySegregationStateDto) {
        this.state = state;
    }

    setBaseCompanies(companies: BaseCompanyDto[]) {
        this.baseCompanies = companies;
    }

    setLoadingBaseCompanies(loadingBaseCompanies: boolean) {
        this.loadingBaseCompanies = loadingBaseCompanies;
    }

    setLoadingState(loadingState: boolean) {
        this.loadingState = loadingState;
    }

    get useBaseCompanySegregation() {
        return this.sessionStore.response?.useBaseCompanySegregation ?? false;
    }

    get isInitializing() {
        return this.sessionStore.isInitializing || this.loadingState || this.state.showBaseCompanySelectModal;
    }

    get switchHidden() {
        return this.isInitializing || !this.useBaseCompanySegregation;
    }

    get baseCompanyOptions() {
        return this.baseCompanies.map(c => {
            const label = `${c.baseCompanyCode} - ${c.baseCompanyName}`;
            return {
                label,
                title: label,
                value: c.baseCompanyId
            };
        });
    }

    get isSelectionDisabled() {
        return this.contractSubmissionStore.contractSent;
    }

    async initialize() {
        await this.fetchBaseCompanies();
    }

    async fetchBaseCompanies() {
        try {
            this.setLoadingBaseCompanies(true);

            const resp = await this.service.getBaseCompanies(this.appStore.sessionExtension, this.appStore.sessionId);

            if (!resp.isOk()) {
                console.error(resp.error.data ? resp.error.data.title : resp.error.text);
                message.error('Failed to fetch base companies');
                return;
            }

            resp.map(companies => this.setBaseCompanies(companies));
        } finally {
            this.setLoadingBaseCompanies(false);
        }
    }

    async getState() {
        try {
            this.setLoadingState(true);

            const resp = await this.service.getBaseCompanySegregationState(
                this.appStore.sessionExtension,
                this.appStore.sessionId
            );

            if (!resp.isOk()) {
                console.error(resp.error.data ? resp.error.data.title : resp.error.text);
                return;
            }

            resp.map(state => this.setState(state));
        } finally {
            this.setLoadingState(false);
        }
    }

    async selectBaseCompany(selectedBaseCompanyId: string) {
        if (this.isSelectionDisabled) {
            return;
        }

        const resp = await this.service.selectBaseCompany(
            this.appStore.sessionExtension,
            this.appStore.sessionId,
            selectedBaseCompanyId
        );

        if (!resp.isOk()) {
            console.error(resp.error.data ? resp.error.data.title : resp.error.text);
            message.error('Failed to select base company');
            return;
        }

        resp.map(state => this.setState(state));
        this.baseCompanyChangeSubject.next({ selectedBaseCompanyId });
    }

    dispose() {
        this.disposeReactions();
    }
}
